import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { StandardTextFieldProps as MuiStandardTextFieldProps } from '@mui/material/TextField';
import MuiTextField from '@mui/material/TextField';
import MuiFormControl from '@mui/material/FormControl';
import MuiLockIcon from '@mui/icons-material/Lock';
import { styled, Theme } from '../styles';
import FormLabel from '../FormLabel';
import FormHelperText from '../FormHelperText';
import useFieldIdDefault, { UseFieldIdReturn } from '../useFieldId';
export interface Props extends MuiStandardTextFieldProps {
  name: string;
  maxLength?: number;
  minLength?: number;
  hasError?: boolean;
  errorHelperText?: string;
  sublabel?: ReactNode;
  icon?: ReactNode;
  textFieldRender?: (textField: ReactNode, isFocused: boolean) => ReactNode;
  hint?: string;
  noAsterisk?: boolean;
  isOptionalShown?: boolean;
  translations?: {
    optional: string;
  };
  useFieldId?: (fieldName: string, id?: string) => UseFieldIdReturn;
}
export const getInputBaseRootStyle = ({
  palette
}: Pick<Theme, 'palette'> | Theme) => ({
  height: 48,
  backgroundColor: palette.form.field.background.enabled,
  width: '100%',
  alignItems: 'flex-center',
  '&.MuiInputBase-multiline': {
    alignItems: 'flex-start',
    minHeight: 48,
    height: 'auto'
  },
  '&:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.form.field.border.focus
  },
  '&:not(.Mui-disabled).Mui-error:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.form.field.border.error.hover
  },
  '&.Mui-disabled': {
    backgroundColor: palette.form.field.background.disabled
  },
  '& .Mui-disabled': {
    WebkitTextFillColor: palette.form.field.input
  },
  '&.Mui-error': {
    backgroundColor: palette.form.field.background.error.enabled
  },
  '& .MuiSvgIcon-root': {
    color: palette.form.selection.icon.minor
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    opacity: 0
  }
});
export const getInputStyle = ({
  palette
}: Pick<Theme, 'palette'> | Theme) => ({
  '& input': {
    padding: 0,
    color: palette.form.field.input,
    '&:disabled::placeholder': {
      color: palette.form.field.placeholder,
      opacity: 1
    },
    '&::placeholder': {
      color: palette.form.field.placeholder,
      opacity: 1
    }
  }
});
export const TextFieldStyled = styled(MuiTextField)(({
  theme: {
    spacing,
    palette
  }
}) => ({
  '&.MuiTextField-root': {
    width: '100%'
  },
  '& .MuiInputBase-root': {
    padding: spacing(1.5, 1),
    ...getInputBaseRootStyle({
      palette
    })
  },
  ...getInputStyle({
    palette
  })
}));

/* istanbul ignore next */
const noop = () => {};
const TextField = ({
  label,
  helperText = '',
  maxLength,
  minLength,
  disabled = false,
  hasError = false,
  errorHelperText = '',
  sublabel,
  icon = null,
  hint = undefined,
  value,
  name,
  id,
  required = false,
  noAsterisk = false,
  isOptionalShown = false,
  multiline = false,
  onChange = noop,
  onBlur = noop,
  onFocus = noop,
  rows = 3,
  useFieldId = useFieldIdDefault,
  inputProps = {},
  InputProps = {},
  translations = {
    optional: 'optional'
  },
  textFieldRender,
  sx = {},
  ref,
  ...otherProps
}: Props) => {
  const [currentLength, setCurrentLength] = useState(typeof value === 'string' ? value.length : 0);
  const [isFilled, setIsFilled] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    setCurrentLength(String(value ?? '').length);
  }, [value]);
  let endAdornment = InputProps.endAdornment ?? null;
  if (icon) {
    endAdornment = icon;
  } else if (disabled && value) {
    endAdornment = <MuiLockIcon data-testid="LockIcon" />;
  }
  const {
    fieldId,
    labelId
  } = useFieldId(name, id);
  const hasHelperText = helperText || hasError && errorHelperText || minLength || maxLength;
  const ariaDescription = hasHelperText ? {
    'aria-describedby': `${fieldId}Helper`
  } : {};
  const textField = <TextFieldStyled variant="outlined" ref={ref} name={name} disabled={disabled} error={hasError} value={value} required={required} multiline={multiline} minRows={rows} onFocus={e => {
    setIsFocused(true);
    onFocus(e);
  }} onBlur={e_0 => {
    setIsFocused(false);
    onBlur(e_0);
  }} InputProps={{
    ...InputProps,
    id: fieldId,
    endAdornment,
    inputProps: {
      maxLength,
      minLength,
      ...inputProps,
      ...ariaDescription
    }
  }} onChange={e_1 => {
    if (maxLength || minLength) {
      setCurrentLength(e_1.target.value?.length);
    }
    setIsFilled(e_1.target.value?.length > 0);
    onChange(e_1);
  }} {...otherProps} />;
  return <MuiFormControl sx={{
    width: 1,
    ...sx
  }}>
      {label && <FormLabel id={labelId} error={hasError} focused={isFocused} disabled={disabled} filled={isFilled} required={required && !noAsterisk} htmlFor={fieldId} component="label" sublabel={sublabel} isOptionalShown={isOptionalShown && !required} translations={translations} hint={hint}>
          {label}
        </FormLabel>}
      {textFieldRender ? textFieldRender(textField, isFocused) : textField}
      {hasHelperText && <FormHelperText helperText={typeof helperText === 'string' ?
    // eslint-disable-next-line react/no-danger
    <span dangerouslySetInnerHTML={{
      __html: helperText
    }} /> : helperText} hasError={hasError}
    // eslint-disable-next-line react/no-danger
    errorHelperText={<span dangerouslySetInnerHTML={{
      __html: errorHelperText
    }} />} minLength={minLength} maxLength={maxLength} currentLength={currentLength} id={`${fieldId}Helper`} />}
    </MuiFormControl>;
};
export default TextField;